import { TextEntity, TextEntityType } from '@wui/im'

import { isMentionAllUserId, MENTION_ALL_USER_ID } from '@/shared/services/mention-helper'

import { sanitizeEntityUrl } from './sanitize-entity-url'
import { HtmlToEntitiesResult } from './types'

const BLOCK_TAGS = new Set(['div', 'p', 'pre', 'li', 'h1', 'h2', 'h3', 'blockquote'])

type OpenEntity = {
  start: number
  type: TextEntityType
  url?: string
  userId?: string
}

const normalizeMentionUserId = (userId: string): string => {
  return isMentionAllUserId(userId) ? MENTION_ALL_USER_ID : userId
}

const getEntityFromElement = (element: HTMLElement): OpenEntity | undefined => {
  const tag = element.tagName.toLowerCase()
  const contactUri = element.getAttribute('data-contact-uri')

  if ((tag === 'b' || tag === 'strong') && contactUri) {
    return { start: 0, type: 'mention', userId: normalizeMentionUserId(contactUri) }
  }

  if (tag === 'b' || tag === 'strong') return { start: 0, type: 'bold' }
  if (tag === 'i' || tag === 'em') return { start: 0, type: 'italic' }
  if (tag === 'u') return { start: 0, type: 'underline' }
  if (tag === 's' || tag === 'strike' || tag === 'del') return { start: 0, type: 'strikethrough' }
  if (tag === 'code') return { start: 0, type: 'code' }
  if (tag === 'pre') return { start: 0, type: 'pre' }

  if (tag === 'a') {
    const href = element.getAttribute('href')
    const url = href ? sanitizeEntityUrl(href) : undefined
    if (!url) return
    return { start: 0, type: 'link', url }
  }

  return
}

const appendText = (state: HtmlToEntitiesResult, value: string) => {
  state.text += value.replace(/\u00A0/g, ' ').replace(/\u200B/g, '')
}

const walk = (node: Node, state: HtmlToEntitiesResult) => {
  if (node.nodeType === Node.TEXT_NODE) {
    appendText(state, node.textContent ?? '')
    return
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return

  const element = node as HTMLElement
  const tag = element.tagName.toLowerCase()

  if (tag === 'br') {
    appendText(state, '\n')
    return
  }

  if (BLOCK_TAGS.has(tag) && state.text.length > 0 && !state.text.endsWith('\n')) {
    appendText(state, '\n')
  }

  const entity = getEntityFromElement(element)
  if (entity) {
    entity.start = state.text.length
  }

  for (const child of Array.from(node.childNodes)) {
    walk(child, state)
  }

  if (entity && state.text.length > entity.start) {
    const result: TextEntity = {
      start: entity.start,
      end: state.text.length,
      type: entity.type,
    }
    if (entity.url) result.url = entity.url
    if (entity.userId) result.userId = entity.userId
    state.entities.push(result)
  }
}

export const htmlToEntities = (html: string): HtmlToEntitiesResult => {
  const state: HtmlToEntitiesResult = {
    text: '',
    entities: [],
  }

  if (!html) return state

  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')
  for (const child of Array.from(doc.body.childNodes)) {
    walk(child, state)
  }

  return state
}
